import { supabase } from '../../lib/supabase';
/**
 * Onboarding Service for Sales CRM
 *
 * Manages user onboarding progress through Sales CRM setup steps.
 * Tracks completion status, progress percentage, and step metadata.
 */
// Default onboarding steps for Sales CRM
export const DEFAULT_ONBOARDING_STEPS = [
    {
        stepId: 'import_contacts',
        title: 'Import Your Contacts',
        description: 'Step 1 of 5',
        order: 1,
        requiredProgress: 100
    },
    {
        stepId: 'organize_people',
        title: 'Organize your Top 50 People',
        description: 'Step 2 of 5',
        order: 2,
        requiredProgress: 100
    },
    {
        stepId: 'send_first_email',
        title: 'Send Your First Email',
        description: 'Step 3 of 5',
        order: 3,
        requiredProgress: 100
    },
    {
        stepId: 'create_first_deal',
        title: 'Create Your First Deal',
        description: 'Step 4 of 5',
        order: 4,
        requiredProgress: 100
    },
    {
        stepId: 'setup_follow_ups',
        title: 'Set Up Follow-up Reminders',
        description: 'Step 5 of 5',
        order: 5,
        requiredProgress: 100
    }
];
export class SalesOnboardingService {
    /**
     * Get all onboarding steps for a user
     */
    async getOnboardingSteps(userId, organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_onboarding_progress')
                .select('*')
                .eq('user_id', userId)
                .eq('organization_id', organizationId)
                .order('step_order', { ascending: true });
            if (error) {
                console.error('Error fetching onboarding steps:', error);
                return [];
            }
            return this.transformSteps(data || []);
        }
        catch (error) {
            console.error('Error in getOnboardingSteps:', error);
            return [];
        }
    }
    /**
     * Get a specific onboarding step
     */
    async getOnboardingStep(userId, organizationId, stepId) {
        try {
            const { data, error } = await supabase
                .from('sales_onboarding_progress')
                .select('*')
                .eq('user_id', userId)
                .eq('organization_id', organizationId)
                .eq('step_id', stepId)
                .single();
            if (error || !data) {
                return null;
            }
            return this.transformStep(data);
        }
        catch (error) {
            console.error('Error in getOnboardingStep:', error);
            return null;
        }
    }
    /**
     * Initialize default onboarding steps for a new user
     */
    async initializeOnboarding(userId, organizationId) {
        try {
            // Check if user already has onboarding steps
            const existing = await this.getOnboardingSteps(userId, organizationId);
            if (existing.length > 0) {
                return existing;
            }
            // Create default steps
            const stepsToCreate = DEFAULT_ONBOARDING_STEPS.map(step => ({
                user_id: userId,
                organization_id: organizationId,
                step_id: step.stepId,
                step_title: step.title,
                step_order: step.order,
                progress: 0,
                completed: false
            }));
            const { data, error } = await supabase
                .from('sales_onboarding_progress')
                .insert(stepsToCreate)
                .select();
            if (error) {
                console.error('Error initializing onboarding:', error);
                throw error;
            }
            return this.transformSteps(data || []);
        }
        catch (error) {
            console.error('Error in initializeOnboarding:', error);
            return [];
        }
    }
    /**
     * Update onboarding step progress
     */
    async updateStepProgress(userId, organizationId, request) {
        try {
            const updates = {
                updated_at: new Date().toISOString()
            };
            if (request.progress !== undefined) {
                updates.progress = Math.min(100, Math.max(0, request.progress));
            }
            if (request.completed !== undefined) {
                updates.completed = request.completed;
                if (request.completed) {
                    updates.completed_at = new Date().toISOString();
                }
            }
            if (request.metadata !== undefined) {
                updates.metadata = request.metadata;
            }
            const { data, error } = await supabase
                .from('sales_onboarding_progress')
                .update(updates)
                .eq('user_id', userId)
                .eq('organization_id', organizationId)
                .eq('step_id', request.stepId)
                .select()
                .single();
            if (error) {
                console.error('Error updating step progress:', error);
                throw error;
            }
            return this.transformStep(data);
        }
        catch (error) {
            console.error('Error in updateStepProgress:', error);
            return null;
        }
    }
    /**
     * Mark a step as completed
     */
    async completeStep(userId, organizationId, stepId) {
        return this.updateStepProgress(userId, organizationId, {
            stepId,
            progress: 100,
            completed: true
        });
    }
    /**
     * Increment step progress
     */
    async incrementProgress(userId, organizationId, stepId, incrementBy = 10) {
        try {
            // Get current step
            const currentStep = await this.getOnboardingStep(userId, organizationId, stepId);
            if (!currentStep) {
                return null;
            }
            const newProgress = Math.min(100, currentStep.progress + incrementBy);
            const isCompleted = newProgress >= 100;
            return this.updateStepProgress(userId, organizationId, {
                stepId,
                progress: newProgress,
                completed: isCompleted
            });
        }
        catch (error) {
            console.error('Error in incrementProgress:', error);
            return null;
        }
    }
    /**
     * Get onboarding completion percentage
     */
    async getOverallProgress(userId, organizationId) {
        try {
            const steps = await this.getOnboardingSteps(userId, organizationId);
            if (steps.length === 0) {
                return 0;
            }
            const totalProgress = steps.reduce((sum, step) => sum + step.progress, 0);
            return Math.round(totalProgress / steps.length);
        }
        catch (error) {
            console.error('Error in getOverallProgress:', error);
            return 0;
        }
    }
    /**
     * Check if onboarding is complete
     */
    async isOnboardingComplete(userId, organizationId) {
        try {
            const steps = await this.getOnboardingSteps(userId, organizationId);
            return steps.length > 0 && steps.every(step => step.completed);
        }
        catch (error) {
            console.error('Error in isOnboardingComplete:', error);
            return false;
        }
    }
    /**
     * Get incomplete steps
     */
    async getIncompleteSteps(userId, organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_onboarding_progress')
                .select('*')
                .eq('user_id', userId)
                .eq('organization_id', organizationId)
                .eq('completed', false)
                .order('step_order', { ascending: true });
            if (error) {
                console.error('Error fetching incomplete steps:', error);
                return [];
            }
            return this.transformSteps(data || []);
        }
        catch (error) {
            console.error('Error in getIncompleteSteps:', error);
            return [];
        }
    }
    /**
     * Reset onboarding progress
     */
    async resetOnboarding(userId, organizationId) {
        try {
            const { error } = await supabase
                .from('sales_onboarding_progress')
                .delete()
                .eq('user_id', userId)
                .eq('organization_id', organizationId);
            if (error) {
                console.error('Error resetting onboarding:', error);
                return false;
            }
            // Reinitialize with default steps
            await this.initializeOnboarding(userId, organizationId);
            return true;
        }
        catch (error) {
            console.error('Error in resetOnboarding:', error);
            return false;
        }
    }
    /**
     * Auto-update progress based on user actions
     */
    async autoUpdateProgress(userId, organizationId, action) {
        try {
            const stepMapping = {
                contact_imported: 'import_contacts',
                email_sent: 'send_first_email',
                deal_created: 'create_first_deal',
                follow_up_created: 'setup_follow_ups'
            };
            const stepId = stepMapping[action];
            if (!stepId)
                return;
            const currentStep = await this.getOnboardingStep(userId, organizationId, stepId);
            if (!currentStep || currentStep.completed) {
                return; // Already complete
            }
            // Mark as complete
            await this.completeStep(userId, organizationId, stepId);
        }
        catch (error) {
            console.error('Error in autoUpdateProgress:', error);
        }
    }
    /**
     * Transform database row to OnboardingStep type
     */
    transformStep(row) {
        return {
            id: row.id,
            stepId: row.step_id,
            userId: row.user_id,
            organizationId: row.organization_id,
            stepTitle: row.step_title,
            stepOrder: row.step_order,
            progress: row.progress,
            completed: row.completed,
            completedAt: row.completed_at ? new Date(row.completed_at) : undefined,
            metadata: row.metadata || {},
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
        };
    }
    /**
     * Transform multiple database rows
     */
    transformSteps(rows) {
        return rows.map(row => this.transformStep(row));
    }
}
// Export singleton instance
export const salesOnboardingService = new SalesOnboardingService();
